import { Node, mergeAttributes } from '@tiptap/core'
import type { RawCommands } from '@tiptap/core'
import { isAllowedUri } from '@tiptap/extension-link'
import { sanitizeCssValue } from '@disco/utils/css-utils'

export type CtaButtonAttributes = {
  text: string
  url: string
  backgroundColor: string
  textColor: string
  borderRadius: string
  padding: string
}

const DEFAULT_BUTTON_ATTRS: CtaButtonAttributes = {
  text: 'Click Here',
  url: '',
  backgroundColor: '#0693e3',
  textColor: '#ffffff',
  borderRadius: '3px',
  padding: '10px 25px',
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    ctaButton: {
      insertCtaButton: (attrs?: Partial<CtaButtonAttributes>) => ReturnType
      updateCtaButton: (attrs: Partial<CtaButtonAttributes>) => ReturnType
    }
  }
}

export const CtaButtonNode = Node.create({
  name: 'ctaButton',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      text: { default: DEFAULT_BUTTON_ATTRS.text },
      url: { default: DEFAULT_BUTTON_ATTRS.url },
      backgroundColor: { default: DEFAULT_BUTTON_ATTRS.backgroundColor },
      textColor: { default: DEFAULT_BUTTON_ATTRS.textColor },
      borderRadius: { default: DEFAULT_BUTTON_ATTRS.borderRadius },
      padding: { default: DEFAULT_BUTTON_ATTRS.padding },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-cta-button]',
        getAttrs: (dom: HTMLElement) => {
          const a = dom.querySelector('a')
          return {
            url: a?.getAttribute('href') || '',
            text: a?.textContent || '',
            backgroundColor:
              a?.style.backgroundColor || DEFAULT_BUTTON_ATTRS.backgroundColor,
            textColor: a?.style.color || DEFAULT_BUTTON_ATTRS.textColor,
            borderRadius:
              a?.style.borderRadius || DEFAULT_BUTTON_ATTRS.borderRadius,
            padding: a?.style.padding || DEFAULT_BUTTON_ATTRS.padding,
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        {
          'data-cta-button': 'true',
          class: 'rte-cta-button-wrapper',
        },
        {},
      ),
      [
        'a',
        {
          href: isAllowedUri(HTMLAttributes.url) ? HTMLAttributes.url : '',
          class: 'rte-cta-button',
          style: `background-color: ${sanitizeCssValue(
            HTMLAttributes.backgroundColor,
          )}; color: ${sanitizeCssValue(
            HTMLAttributes.textColor,
          )}; border-radius: ${sanitizeCssValue(
            HTMLAttributes.borderRadius,
          )}; padding: ${sanitizeCssValue(
            HTMLAttributes.padding,
          )}; text-decoration: none; display: inline-block;`,
          target: '_blank',
          rel: 'noopener noreferrer',
        },
        HTMLAttributes.text,
      ],
    ]
  },

  addCommands() {
    return {
      insertCtaButton:
        (attrs?: Partial<CtaButtonAttributes>) =>
        ({ commands }) => {
          return commands.insertContent({
            type: 'ctaButton',
            attrs: { ...DEFAULT_BUTTON_ATTRS, ...attrs },
          })
        },
      updateCtaButton:
        (attrs: Partial<CtaButtonAttributes>) =>
        ({ tr, state, dispatch }) => {
          const { selection } = state
          const node = state.doc.nodeAt(selection.from)
          if (node?.type.name !== 'ctaButton') return false

          if (dispatch) {
            tr.setNodeMarkup(selection.from, undefined, {
              ...node.attrs,
              ...attrs,
            })
          }
          return true
        },
    } as Partial<RawCommands>
  },
})
